<template>
  <main
    class="page"
    role="main"
    aria-labelledby="ticket-h1"
  >
    <div class="page-header">
      <h1
        id="ticket-h1"
        class="h1"
      >
        Ticket #{{ ticket?.id }} – {{ ticket?.subject || '—' }}
      </h1>
      <div style="display:flex;gap:8px;align-items:center;">
        <select
          v-model="status"
          :disabled="saving"
          @change="onStatusChange"
        >
          <option value="open">
            Offen
          </option>
          <option value="in_progress">
            In Bearbeitung
          </option>
          <option value="resolved">
            Gelöst
          </option>
          <option value="closed">
            Geschlossen
          </option>
        </select>
        <button
          class="btn"
          @click="back"
        >
          Zur Liste
        </button>
      </div>
    </div>

    <div
      class="card thread"
      aria-live="polite"
    >
      <div
        v-for="m in messages"
        :key="m.id"
        :class="['msg', me(m) ? 'me' : 'other']"
      >
        <div class="bubble">
          <div class="meta">
            <span class="author">{{ me(m) ? 'Ich' : (m.userName || m.user_name || 'Nutzer') }}</span>
            <span class="time">{{ time(m.createdAt || m.created_at) }}</span>
          </div>
          <p
            v-if="m.message"
            class="text"
          >
            {{ m.message }}
          </p>
          <div
            v-if="(m.attachments || []).length"
            class="attachments"
          >
            <button
              v-for="a in m.attachments"
              :key="a.id"
              class="btn"
              :title="a.filename"
              @click="download(a)"
            >
              📎 {{ a.filename }}
            </button>
          </div>
        </div>
      </div>

      <p
        v-if="messages.length === 0"
        class="muted"
      >
        Keine Nachrichten.
      </p>
    </div>

    <div class="card composer">
      <form
        class="form"
        @submit.prevent="send"
      >
        <div class="field">
          <label for="msg">Nachricht</label>
          <textarea
            id="msg"
            v-model="text"
            rows="3"
            required
          />
        </div>
        <div class="field">
          <label for="files">Anhänge</label>
          <input
            id="files"
            ref="fileInput"
            type="file"
            multiple
            @change="onFiles"
          >
          <p
            v-if="files.length"
            class="help"
          >
            {{ files.length }} Datei(en) ausgewählt
          </p>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;">
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="saving"
          >
            <span
              v-if="saving"
              class="spinner"
              aria-hidden="true"
            />
            Senden
          </button>
        </div>
        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
        <p
          v-if="success"
          class="notice notice-success mt-12"
          role="status"
        >
          Gesendet.
        </p>
      </form>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTicketsStore } from '@/stores/tickets';

const route = useRoute();
const router = useRouter();
const store = useTicketsStore();

const id = computed(() => Number(route.params.id));
const ticket = computed(() => store.current);
const messages = computed(() => (ticket.value?.messages || []));
const status = ref('open');

const text = ref('');
const files = ref([]);
const fileInput = ref(null);

const saving = ref(false);
const error = ref('');
const success = ref(false);

function me(m) {
  const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id;
  return uid && (m.userId === uid || m.user_id === uid);
}

function time(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(d);
}

function back() {
  router.push({ name: 'Tickets' });
}

function onFiles(e) {
  files.value = Array.from(e.target.files || []);
}

async function send() {
  error.value = '';
  success.value = false;
  saving.value = true;
  try {
    await store.reply(id.value, { message: text.value, attachments: files.value });
    text.value = '';
    files.value = [];
    if (fileInput.value) fileInput.value.value = '';
    success.value = true;
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Senden fehlgeschlagen.';
  } finally {
    saving.value = false;
  }
}

async function download(a) {
  try {
    const res = await store.downloadAttachment(ticket.value.id, a.id);
    const blob = new Blob([res.data], { type: a.mime_type || a.mimeType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const el = document.createElement('a');
    el.href = url;
    el.download = a.filename || `anhang-${a.id}`;
    document.body.appendChild(el);
    el.click();
    el.remove();
    URL.revokeObjectURL(url);
  } catch (e) {
    // still: keine harte Fehlermeldung nötig
  }
}

async function load() {
  await store.fetchTicket(id.value);
  status.value = ticket.value?.status || 'open';
}

async function onStatusChange() {
  await store.setStatus(id.value, status.value);
}

onMounted(load);
</script>

<style scoped>
.page { padding:16px; background:var(--bg); color:var(--text); }
.page-header { display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--border); padding-bottom:8px; margin-bottom:12px; }
.card { background:var(--surface); border:1px solid var(--border); border-radius:10px; padding:16px; }

.thread { max-height: calc(100vh - 300px); overflow:auto; }
.msg { display:flex; margin-bottom:12px; }
.msg.me { justify-content: flex-end; }
.bubble {
  max-width: 70%;
  border:1px solid var(--border);
  border-radius: 12px;
  padding: 10px 12px;
  background: var(--surface);
  box-shadow: 0 1px 2px rgba(0,0,0,.04);
}
.msg.me .bubble {
  background: color-mix(in srgb, var(--primary) 8%, white);
  border-color: color-mix(in srgb, var(--primary) 40%, var(--border));
}
.meta { display:flex; gap:8px; align-items:center; font-size:12px; color:var(--muted); margin-bottom:4px; }
.text { margin: 0; white-space: pre-wrap; word-wrap: break-word; }
.attachments { display:flex; gap:8px; flex-wrap: wrap; margin-top:6px; }

.form { display:flex; flex-direction:column; gap:12px; }

.spinner { width:16px; height:16px; border:2px solid color-mix(in srgb, var(--primary) 30%, transparent); border-top-color:var(--primary); border-radius:50%; margin-right:6px; animation: spin .9s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
